package co.maytes.standupbot.dashboard.pages.app.orgteams

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.layout.AdminGuard
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.TextInputStyle
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.settings.rememberSettingsStore
import co.maytes.standupbot.dashboard.features.teams.TeamsListPhase
import co.maytes.standupbot.dashboard.features.teams.rememberTeamsStore
import co.maytes.standupbot.dashboard.features.teams.viewPhase
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.shared.dto.team.TeamDto
import co.maytes.standupbot.shared.dto.team.UpsertTeamRequestDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@Page("/app/org-teams")
@Composable
fun OrgTeamsIndexPage() = AuthGuard {
    AdminGuard {
        AppShell {
            OrgTeamsScreen()
        }
    }
}

@Composable
private fun OrgTeamsScreen() {
    val auth = rememberAuthStore()
    val store = rememberTeamsStore()
    val settingsStore = rememberSettingsStore()
    val scope = rememberCoroutineScope()

    var creating by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<TeamDto?>(null) }
    var deleteTarget by remember { mutableStateOf<TeamDto?>(null) }
    var deleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }

    val workspaceId = auth.currentUser?.workspace?.id
    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            store.refresh(workspaceId)
            settingsStore.refreshMembers(workspaceId)
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(Layout.wide),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppPageHeader(
                title = "Teams",
                eyebrow = "admin",
                subtitle = "Org-level groups of workspace members. Use these to organise people independently of standup schedules.",
                action = {
                    Button(
                        label = "New team",
                        variant = ButtonVariant.Primary,
                        enabled = !workspaceId.isNullOrBlank(),
                        onClick = { creating = true },
                    )
                },
            )
            StateSwap(
                state = store.phase.viewPhase(isEmpty = store.list.isEmpty()),
                modifier = Modifier.fillMaxWidth().margin(top = Spacing.md),
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(Spacing.md),
                ) {
                    when (store.phase) {
                        TeamsListPhase.Idle, TeamsListPhase.Loading ->
                            SpanText(
                                "Loading teams…",
                                modifier = Modifier.color(Palette.textSecondary).fontSize(Typography.body.size),
                            )
                        TeamsListPhase.Failed ->
                            SpanText(
                                store.lastError ?: "Failed to load teams.",
                                modifier = Modifier.color(Palette.error).fontSize(Typography.body.size),
                            )
                        TeamsListPhase.Ready ->
                            if (store.list.isEmpty()) {
                                SpanText(
                                    "No teams yet.",
                                    modifier = Modifier.color(Palette.textSecondary).fontSize(Typography.body.size),
                                )
                            } else {
                                store.list.forEach { team ->
                                    TeamRow(
                                        team = team,
                                        onEdit = { editing = team },
                                        onDelete = {
                                            deleteError = null
                                            deleteTarget = team
                                        },
                                    )
                                }
                            }
                    }
                }
            }
        }
    }

    if (creating && !workspaceId.isNullOrBlank()) {
        TeamFormModal(
            title = "New team",
            initial = null,
            members = settingsStore.members,
            onDismiss = { creating = false },
            onSubmit = { body, setError, setSubmitting ->
                scope.launch {
                    setSubmitting(true)
                    when (val r = store.create(workspaceId, body)) {
                        is Result.Ok -> {
                            store.refresh(workspaceId)
                            creating = false
                        }
                        is Result.Err -> {
                            setSubmitting(false)
                            setError(r.error.displayMessage)
                        }
                    }
                }
            },
        )
    }

    editing?.let { team ->
        if (!workspaceId.isNullOrBlank()) {
            TeamFormModal(
                title = "Edit team",
                initial = team,
                members = settingsStore.members,
                onDismiss = { editing = null },
                onSubmit = { body, setError, setSubmitting ->
                    scope.launch {
                        setSubmitting(true)
                        when (val r = store.update(workspaceId, team.id, body)) {
                            is Result.Ok -> {
                                store.refresh(workspaceId)
                                editing = null
                            }
                            is Result.Err -> {
                                setSubmitting(false)
                                setError(r.error.displayMessage)
                            }
                        }
                    }
                },
            )
        }
    }

    deleteTarget?.let { team ->
        if (!workspaceId.isNullOrBlank()) {
            ConfirmModal(
                title = "Delete ${team.name}?",
                body = deleteError
                    ?: "Members stay in the workspace; only this grouping is removed. This cannot be undone.",
                confirmLabel = if (deleting) "Deleting…" else "Delete",
                cancelLabel = "Keep",
                tone = ConfirmTone.Danger,
                confirmEnabled = !deleting,
                onDismiss = {
                    deleteTarget = null
                    deleteError = null
                    deleting = false
                },
                onConfirm = {
                    scope.launch {
                        deleting = true
                        deleteError = null
                        when (val r = store.delete(workspaceId, team.id)) {
                            is Result.Ok -> {
                                store.refresh(workspaceId)
                                deleting = false
                                deleteTarget = null
                            }
                            is Result.Err -> {
                                deleting = false
                                deleteError = r.error.displayMessage
                            }
                        }
                    }
                },
            )
        }
    }
}

@Composable
private fun TeamRow(
    team: TeamDto,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.sm, leftRight = Spacing.md)
            .borderRadius(Radius.md)
            .styleModifier {
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.gap(Spacing.xxs)) {
            SpanText(
                text = team.name,
                modifier = Modifier.fontSize(Typography.h3.size).color(Palette.textPrimary),
            )
            team.description?.takeIf { it.isNotBlank() }?.let { d ->
                SpanText(text = d, modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary))
            }
        }
        Row(
            modifier = Modifier.gap(Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = "${team.memberIds.size} member${if (team.memberIds.size == 1) "" else "s"}",
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
            )
            Button(
                label = "Edit",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                onClick = onEdit,
            )
            Button(
                label = "Delete",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                onClick = onDelete,
            )
        }
    }
}

@Composable
private fun TeamFormModal(
    title: String,
    initial: TeamDto?,
    members: List<WorkspaceMemberDto>,
    onDismiss: () -> Unit,
    onSubmit: (
        body: UpsertTeamRequestDto,
        setError: (String?) -> Unit,
        setSubmitting: (Boolean) -> Unit,
    ) -> Unit,
) {
    var name by remember(initial?.id) { mutableStateOf(initial?.name ?: "") }
    var description by remember(initial?.id) { mutableStateOf(initial?.description ?: "") }
    val selected = remember(initial?.id) { mutableStateListOf<String>().apply { addAll(initial?.memberIds ?: emptyList()) } }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }

    val nameValid = name.isNotBlank()

    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("background-color", "rgba(9, 9, 11, 0.55)")
                property("backdrop-filter", "blur(4px)")
                property("z-index", "${ZIndex.modal}")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onClick { onDismiss() },
    ) {
        Column(
            modifier = Modifier
                .backgroundColor(Palette.surface)
                .borderRadius(Radius.lg)
                .padding(Spacing.lg)
                .width(520.px)
                .styleModifier {
                    property("max-width", "calc(100vw - 48px)")
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("box-shadow", Shadow.LG)
                }
                .onClick { evt -> evt.stopPropagation() },
        ) {
            SpanText(
                text = title,
                modifier = Modifier
                    .fontSize(Typography.h2.size)
                    .fontWeight(Typography.h2.weight)
                    .color(Palette.textPrimary)
                    .margin(bottom = Spacing.lg),
            )

            FieldLabel("Name")
            Input(
                type = InputType.Text,
                attrs = TextInputStyle.toModifier()
                    .fillMaxWidth()
                    .margin(bottom = Spacing.md)
                    .toAttrs {
                        placeholder("Platform Engineering")
                        value(name)
                        onInput { name = it.value }
                    },
            )

            FieldLabel("Description")
            Input(
                type = InputType.Text,
                attrs = TextInputStyle.toModifier()
                    .fillMaxWidth()
                    .margin(bottom = Spacing.md)
                    .toAttrs {
                        placeholder("Optional — what this group is for")
                        value(description)
                        onInput { description = it.value }
                    },
            )

            FieldLabel("Members")
            MemberPicker(
                members = members,
                selected = selected,
                onToggle = { userId ->
                    if (selected.contains(userId)) selected.remove(userId) else selected.add(userId)
                },
            )

            errorMessage?.let { msg ->
                SpanText(
                    text = msg,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.error)
                        .margin(top = Spacing.sm),
                )
            }

            Row(
                modifier = Modifier
                    .gap(Spacing.xs)
                    .margin(top = Spacing.lg)
                    .styleModifier {
                        property("width", "100%")
                        property("justify-content", "flex-end")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Cancel",
                    variant = ButtonVariant.Ghost,
                    onClick = onDismiss,
                )
                Button(
                    label = if (submitting) "Saving…" else "Save team",
                    variant = ButtonVariant.Primary,
                    enabled = nameValid && !submitting,
                    onClick = {
                        onSubmit(
                            UpsertTeamRequestDto(
                                name = name.trim(),
                                description = description.trim().ifBlank { null },
                                memberIds = selected.toList(),
                            ),
                            { errorMessage = it },
                            { submitting = it },
                        )
                    },
                )
            }
        }
    }
}

@Composable
private fun MemberPicker(
    members: List<WorkspaceMemberDto>,
    selected: List<String>,
    onToggle: (String) -> Unit,
) {
    if (members.isEmpty()) {
        SpanText(
            text = "No workspace members to add yet.",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        return
    }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(Radius.md)
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("max-height", "220px")
                property("overflow-y", "auto")
            },
    ) {
        members.forEach { member ->
            val isSelected = selected.contains(member.userId)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
                    .cursor(Cursor.Pointer)
                    .gap(Spacing.sm)
                    .styleModifier {
                        if (isSelected) property("background-color", Palette.accentSubtle.cssRgb())
                    }
                    .onClick { onToggle(member.userId) },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .width(16.px)
                        .borderRadius(Radius.sm)
                        .styleModifier {
                            property("height", "16px")
                            property("flex-shrink", "0")
                            property(
                                "border",
                                if (isSelected) "1px solid ${Palette.accent.cssRgb()}" else "1px solid ${Palette.border.cssRgb()}",
                            )
                            property("background-color", if (isSelected) Palette.accent.cssRgb() else "transparent")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    if (isSelected) {
                        SpanText(
                            text = "✓",
                            modifier = Modifier
                                .fontSize(Typography.caption.size)
                                .color(Palette.onPrimary),
                        )
                    }
                }
                SpanText(
                    text = member.displayName,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.textPrimary),
                )
            }
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(600)
            .color(Palette.textPrimary)
            .margin(bottom = Spacing.xxs),
    )
}
